import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { latestRPiImage } from 'modules/crowdcounting/api/crowd_counting';

const SoftImageLine = ({
  uuid,
  coordinates,
  setCoordinates, // Only called on Mouse Up now
  isReversed,
  setIsReversed
}) => {
  const [imgSize, setImgSize] = useState({ width: 0, height: 0 });
  const [imgLoaded, setImgLoaded] = useState(false);
  const [dragging, setDragging] = useState(null);

  // Local state for smooth dragging without triggering parent updates
  const [tempCoordinates, setTempCoordinates] = useState(coordinates);

  const containerRef = useRef(null);

  // --- 2. Sync Props to Local State ---
  // If the parent updates coordinates (e.g. API load), sync our local state.
  // But strictly IGNORE this if we are currently dragging to prevent jumping.
  useEffect(() => {
    if (!dragging && coordinates) {
      setTempCoordinates(coordinates);
    }
  }, [coordinates, dragging]);

  // --- 3. Fetch Image ---

  const { isLoading, isError, data, isFetching } = useQuery({
    queryKey: ['latestRPiImage', uuid],
    queryFn: async () => {
      const data = await latestRPiImage(uuid);
      const blob = new Blob([data], { type: 'image/png' });
      const url = URL.createObjectURL(blob);
      return url;
    },
    refetchInterval: 30000,
  });

  const handleImageLoad = (e) => {
    setImgSize({
      width: e.target.naturalWidth,
      height: e.target.naturalHeight
    });
    setImgLoaded(true);
  };

  // --- 4. Math for Labels ---
  // We use tempCoordinates here so labels move smoothly while dragging
  const labelData = useMemo(() => {
    if (!tempCoordinates) return null;
    const { x1, y1, x2, y2 } = tempCoordinates;

    const mx = (x1 + x2) / 2;
    const my = (y1 + y2) / 2;
    const dx = x2 - x1;
    const dy = y2 - y1;
    const len = Math.sqrt(dx * dx + dy * dy) || 1;

    const nx = -dy / len;
    const ny = dx / len;
    const dist = 40;

    const posA = { x: mx + nx * dist, y: my + ny * dist };
    const posB = { x: mx - nx * dist, y: my - ny * dist };

    return {
      in: isReversed ? posB : posA,
      out: isReversed ? posA : posB,
    };
  }, [tempCoordinates, isReversed]);


  // --- 5. Drag Logic ---
  const getMousePosInImage = (clientX, clientY) => {
    if (!containerRef.current) return { x: 0, y: 0 };
    const rect = containerRef.current.getBoundingClientRect();
    const scaleX = imgSize.width / rect.width;
    const scaleY = imgSize.height / rect.height;
    return {
      x: (clientX - rect.left) * scaleX,
      y: (clientY - rect.top) * scaleY
    };
  };

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!dragging || !tempCoordinates) return;
      const { clientX, clientY } = e.touches ? e.touches[0] : e;
      const pos = getMousePosInImage(clientX, clientY);

      const cx = Math.max(0, Math.min(pos.x, imgSize.width));
      const cy = Math.max(0, Math.min(pos.y, imgSize.height));

      // Update LOCAL state only (smooth, fast)
      if (dragging === 'p1') {
        setTempCoordinates(prev => ({ ...prev, x1: cx, y1: cy }));
      } else if (dragging === 'p2') {
        setTempCoordinates(prev => ({ ...prev, x2: cx, y2: cy }));
      }
    };

    const handleMouseUp = () => {
      if (dragging) {
        // Update PARENT state only when done
        setCoordinates(tempCoordinates);
        setDragging(null);
      }
    };

    if (dragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      window.addEventListener('touchmove', handleMouseMove);
      window.addEventListener('touchend', handleMouseUp);
    }
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('touchmove', handleMouseMove);
      window.removeEventListener('touchend', handleMouseUp);
    };
  }, [dragging, tempCoordinates, imgSize, setCoordinates]);


  // --- 6. Styles ---
  const styles = {
    container: {
      backgroundColor: '#e0e5ec',
      borderRadius: '20px',
      // padding: '24px',
      // boxShadow: '9px 9px 16px rgb(163,177,198,0.6), -9px -9px 16px rgba(255,255,255, 0.5)',
      display: 'inline-flex',
      flexDirection: 'column',
      userSelect: 'none',
      touchAction: 'none',
    },
    imageWrapper: {
      position: 'relative',
      borderRadius: '16px',
      overflow: 'hidden',
      boxShadow: 'inset 6px 6px 12px #b8b9be, inset -6px -6px 12px #ffffff',
      lineHeight: 0,
    },
    image: {
      display: 'block',
      maxWidth: '100%',
      height: 'auto',
      borderRadius: '16px',
      opacity: imgLoaded ? 1 : 0,
      transition: 'opacity 0.4s ease',
      pointerEvents: 'none'
    },
    svgOverlay: {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      zIndex: 10,
    },
    line: {
      stroke: '#ff4d4d', strokeWidth: 5, strokeLinecap: 'round',
      filter: 'drop-shadow(2px 2px 2px rgba(0,0,0,0.3))'
    },
    handle: {
      fill: '#e0e5ec', stroke: '#ff4d4d', strokeWidth: 15,
      cursor: 'grab', filter: 'drop-shadow(2px 2px 3px rgba(0,0,0,0.2))'
    },
    text: {
      fontFamily: 'sans-serif', fontSize: '24px', fontWeight: 'bold',
      textAnchor: 'middle', dominantBaseline: 'middle', cursor: 'pointer',
      filter: 'drop-shadow(1px 1px 2px rgba(0,0,0,0.8))',
    }
  };

  if (isError) return <div>Error Loading Image</div>;

  return (
    <div style={styles.container}>
      <div style={styles.imageWrapper} ref={containerRef}>

        {!imgLoaded && <div style={{ padding: 20 }}>Loading...</div>}

        <img
          src={data}
          alt="Target"
          style={styles.image}
          onLoad={handleImageLoad}
          draggable={false}
        />

        {imgLoaded && tempCoordinates && labelData && (
          <svg
            style={styles.svgOverlay}
            viewBox={`0 0 ${imgSize.width} ${imgSize.height}`}
            preserveAspectRatio="none"
          >
            {/* 1. Line (Uses tempCoordinates) */}
            <line
              x1={tempCoordinates.x1} y1={tempCoordinates.y1}
              x2={tempCoordinates.x2} y2={tempCoordinates.y2}
              style={styles.line}
            />

            {/* 2. Handles */}
            <circle
              cx={tempCoordinates.x1} cy={tempCoordinates.y1} r={10}
              style={styles.handle}
              onMouseDown={(e) => { e.stopPropagation(); setDragging('p1'); }}
              onTouchStart={(e) => { e.stopPropagation(); setDragging('p1'); }}
            />
            <circle
              cx={tempCoordinates.x2} cy={tempCoordinates.y2} r={10}
              style={styles.handle}
              onMouseDown={(e) => { e.stopPropagation(); setDragging('p2'); }}
              onTouchStart={(e) => { e.stopPropagation(); setDragging('p2'); }}
            />

            {/* 3. Labels (Click to Swap) */}
            <g onClick={() => setIsReversed(!isReversed)} style={{ cursor: 'pointer' }}>
              <text x={labelData.in.x} y={labelData.in.y} style={{ ...styles.text, fill: '#4ade80' }}>IN</text>
              <text x={labelData.out.x} y={labelData.out.y} style={{ ...styles.text, fill: '#f87171' }}>OUT</text>
            </g>

          </svg>
        )}
      </div>
    </div>
  );
};

export default SoftImageLine;